import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import axios from 'axios'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { isBefore, parseISO } from 'date-fns'
import { languageNames, providerSlug, toPT } from './helpers'

type NextAvailableSlot = {
  providerEmrId: string
  nextAvailableSlotTimes: {
    video: string | null
    office: string | null
  }
}

interface GetNextAvailableSlotResponse {
  data: { nextAvailableSlotsByProvider: Array<NextAvailableSlot> }
}

type ResponseSpecialtyAreaListItem = {
  _id: string
  name: string
  key: string
  serviceArea: string
  teamPageOrder: number
}

type ResponseSpecialtiesListItem = {
  key: string
  name: string
  specialtyAreaKeys?: string[]
}

type ResponseProviderListItem = {
  providerEpicId: string
  displayName: string
  providerTitle: string
  languages: Array<string>
  image: string
  firstName: string
  lastName: string
  serviceArea: string
  locations: Array<{ name: string }>
  preferences: Array<string>
  specialtyAreaKeys?: Array<string>
  gender: string
  minimumPatientAge: number
  maximumPatientAge: number
  bio: string
}

type SpecialtyAreaListItem = {
  key: string
  name: string
  serviceArea: string
  teamPageOrder: number
}

export type SpecialtyListItem = { groupBy: string; label: string }

type ResponseArrays = {
  providers?: Array<ResponseProviderListItem>
  specialties?: Array<ResponseSpecialtiesListItem>
  specialtyAreas?: Array<ResponseSpecialtyAreaListItem>
}

type Response = {
  MPC: ResponseArrays
  MTL: ResponseArrays
  WEL: ResponseArrays
  ENDO: ResponseArrays
  PAIN?: ResponseArrays
}

export type ProviderListItem = {
  providerEpicId: string
  displayName: string
  displayTitle: string
  languages: Array<string>
  image: string
  slug: string
  locations: Array<string>
  serviceArea: string
  specialtyAreaKeys: Array<string>
  specialties: Array<string>
  gender: string
  ageRange: string
  minAge: number
  maxAge: number
  nextVideoSlot: Date | null
  nextOfficeSlot: Date | null
  specialtyAreaNames: string
  bio: string
}

export type ProviderList = Array<ProviderListItem>

export type SpecialtyAreaList = Array<SpecialtyAreaListItem>

export type SpecialtyList = Array<SpecialtyListItem>

const hiddenProviders = ['vania-zuniga']

const cacheTime = 60 * 60 * 1000

const MAX_AGE = 160

const metaEndpoint = 'https://api.perlmanclinic.app/api/schedule/multi-meta'
const slotsEndpoint = 'https://api.perlmanclinic.app/api/schedule/next-available-slots/providers'

const ALL_SERVICE_AREAS_QUERY_PARAMS = '[]serviceArea=MPC&[]serviceArea=MTL&[]serviceArea=WEL&[]serviceArea=ENDO&[]serviceArea=PAIN'

const buildAgeRange = (min: number, max: number): string => {
  if (min === 0 && max === MAX_AGE) return 'All'
  if (max === MAX_AGE) return `${min}+`
  return `${min} - ${max}`
}

// Uses binary search to match each service to its slots, which are expected to
// come sorted in ascending order by service ID.
const findProviderSlots = (providerEmrId: string, slots: Array<NextAvailableSlot>) => {
  let min = 0
  let max = slots.length - 1
  let guessIdx

  while (min <= max) {
    guessIdx = Math.floor((max + min) / 2)
    if (slots[guessIdx].providerEmrId === providerEmrId) {
      return slots[guessIdx]
    }
    if (slots[guessIdx].providerEmrId < providerEmrId) {
      min = guessIdx + 1
    } else {
      max = guessIdx - 1
    }
  }
  return null
}

const formatData = (
  slots: Array<NextAvailableSlot>,
  data?: {
    providers?: ResponseProviderListItem[]
    specialties?: ResponseSpecialtiesListItem[]
    specialtyAreas?: SpecialtyAreaListItem[]
  }
) =>
  (data && data.providers
    ? data.providers.map((prov) => {
      const providerSlots = findProviderSlots(prov.providerEpicId, slots)?.nextAvailableSlotTimes

      return {
        ...prov,
        specialtyAreaKeys: prov.specialtyAreaKeys || [],
        slug: providerSlug(prov.firstName, prov.lastName),
        displayTitle: prov.providerTitle,
        bio: prov.bio,
        specialties: prov.preferences
          .map(
            (pref) =>
              data.specialties?.find((spec) => spec.key === pref)?.name || ''
          )
          .filter((pref) => pref),
        specialtyAreaNames: data.specialtyAreas
          ?.filter((sa) => prov.specialtyAreaKeys?.includes(sa.key))
          .map((sa) => sa.name)
          .join(', ') || '',
        locations: prov.locations.map((loc) => loc.name),
        languages: prov.languages.map(
          (lang) =>
            languageNames(lang)[0].toUpperCase() +
            languageNames(lang).slice(1)
        ),
        ageRange: buildAgeRange(
          prov.minimumPatientAge,
          prov.maximumPatientAge
        ),
        minAge: prov.minimumPatientAge,
        maxAge: prov.maximumPatientAge,
        nextVideoSlot: providerSlots?.video ? toPT(parseISO(providerSlots.video)) : null,
        nextOfficeSlot: providerSlots?.office ? toPT(parseISO(providerSlots.office)) : null,
      }
    })
    : []
).filter((prov) => !hiddenProviders.includes(prov.slug))

export const useFetchProviderList = (): {
  data: ProviderList
  error: object | null
  isLoading: Boolean
} => {
  const [returnedData, setReturnedData] = useState<ProviderList>([])

  const { isLoading, error, data } = useQuery<Response, Error>(
    'locationList',
    () =>
      fetch(
        `${metaEndpoint}?${ALL_SERVICE_AREAS_QUERY_PARAMS}`
      ).then((res) => res.json()),
    { cacheTime }
  )

  const {
    isLoading: slotsIsLoading,
    error: slotsError,
    data: slotsData
  } = useQuery<Array<NextAvailableSlot>, Error>(
    'slots',
    () =>
      axios
        .get(slotsEndpoint)
        .then(
          (res: GetNextAvailableSlotResponse) => res.data.nextAvailableSlotsByProvider
        )
        .catch(() => [])
  )

  useDeepCompareEffect(() => {
    setReturnedData(
      formatData(slotsData || [], data?.MPC)
        .concat(formatData(slotsData || [], data?.WEL))
        .concat(formatData(slotsData || [], data?.MTL))
        .concat(formatData(slotsData || [], data?.ENDO))
        .concat(formatData(slotsData || [], data?.PAIN))
        .sort((a, b) => {
          if (!a.nextVideoSlot && !b.nextVideoSlot) {
            return a.lastName.localeCompare(b.lastName)
          }
          if (!a.nextVideoSlot) {
            return 1
          }
          if (!b.nextVideoSlot) {
            return -1
          }
          return isBefore(a.nextVideoSlot, b.nextVideoSlot) ? -1 : 1
        })
    )
  }, [data, slotsData || []])

  return {
    isLoading: isLoading || slotsIsLoading,
    error: error && slotsError,
    data: returnedData,
  }
}

export const useFetchSpecialtyList = (): SpecialtyList => {
  const [returnedData, setReturnedData] = useState<SpecialtyList>([])

  const { data } = useQuery<Response, Error>(
    'locationList',
    () =>
      fetch(
        `${metaEndpoint}?${ALL_SERVICE_AREAS_QUERY_PARAMS}`
      ).then((res) => res.json()),
    { cacheTime }
  )

  useDeepCompareEffect(() => {
    const specialtyList = [] as SpecialtyList
    data?.MPC.specialties?.forEach((sp) =>
      sp.specialtyAreaKeys?.forEach((areaKey) =>
        specialtyList.push({ label: sp.name, groupBy: areaKey })
      )
    )
    data?.WEL.specialties?.forEach((sp) =>
      sp.specialtyAreaKeys?.forEach((areaKey) =>
        specialtyList.push({ label: sp.name, groupBy: areaKey })
      )
    )
    data?.MTL.specialties?.forEach((sp) =>
      sp.specialtyAreaKeys?.forEach((areaKey) =>
        specialtyList.push({ label: sp.name, groupBy: areaKey })
      )
    )
    data?.ENDO.specialties?.forEach((sp) =>
      sp.specialtyAreaKeys?.forEach((areaKey) =>
        specialtyList.push({ label: sp.name, groupBy: areaKey })
      )
    )
    data?.PAIN?.specialties?.forEach((sp) =>
      sp.specialtyAreaKeys?.forEach((areaKey) =>
        specialtyList.push({ label: sp.name, groupBy: areaKey })
      )
    )
    setReturnedData(specialtyList)
  }, [data || []])

  return returnedData
}

export const useFetchSpecialtyAreasList = (): SpecialtyAreaList => {
  const [returnedData, setReturnedData] = useState<SpecialtyAreaList>([])

  const { data } = useQuery<Response, Error>(
    'locationList',
    () =>
      fetch(
        `${metaEndpoint}?${ALL_SERVICE_AREAS_QUERY_PARAMS}`
      ).then((res) => res.json()),
    { cacheTime }
  )

  useDeepCompareEffect(() => {
    setReturnedData(
      [
        ...(data?.MPC.specialtyAreas?.map(({ key, name, serviceArea, teamPageOrder }) => ({
          key,
          name,
          serviceArea,
          teamPageOrder,
        })) || []),
        ...(data?.WEL.specialtyAreas?.map(({ key, name, serviceArea, teamPageOrder }) => ({
          key,
          name,
          serviceArea,
          teamPageOrder,
        })) || []),
        ...(data?.MTL.specialtyAreas?.map(({ key, name, serviceArea, teamPageOrder }) => ({
          key,
          name,
          serviceArea,
          teamPageOrder,
        })) || []),
        ...(data?.ENDO.specialtyAreas?.map(({ key, name, serviceArea, teamPageOrder }) => ({
          key,
          name,
          serviceArea,
          teamPageOrder,
        })) || []),
        ...(data?.PAIN?.specialtyAreas?.map(({ key, name, serviceArea, teamPageOrder }) => ({
          key,
          name,
          serviceArea,
          teamPageOrder,
        })) || []),
      ].sort((a, b) => (a.teamPageOrder > b.teamPageOrder ? -1 : 1))
    )
  }, [data || []])

  return returnedData
}

export const useDebounce = (value: string, delay: number = 250): string => {
  const [debouncedValue, setDebouncedValue] = useState<string>(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

export type Filters = {
  search: string
  specialtyArea: string
  specialty: string
  gender: string
  language: string
  location: string
  age: string
  title: string
}

export const useFilters = (
  providerList: ProviderList,
  specialtyAreaList: SpecialtyAreaList,
  specialtyList: SpecialtyList
): {
  filteredList: ProviderList
  handleFilterChange: (value: { [key: string]: string }) => void
  specialtyAreaOptions: SpecialtyAreaList
  specialtyOptions: SpecialtyList
  languageOptions: Array<string>
  locationOptions: Array<string>
  nameOptions: Array<string>
  titleOptions: Array<string>
  filtersApplied: Filters
} => {
  const [filters, setFilters] = useState<Filters>({
    search: '',
    specialtyArea: '',
    specialty: '',
    gender: '',
    language: '',
    location: '',
    age: '',
    title: '',
  })
  const [filteredList, setFilteredList] = useState<ProviderList>([])
  const [specialtyAreaOptions, setSpecialtyAreaOptions] =
    useState<SpecialtyAreaList>([])
  const [specialtyOptions, setSpecialtyOptions] = useState<SpecialtyList>([])
  const [languageOptions, setLanguageOptions] = useState<Array<string>>([])
  const [locationOptions, setLocationOptions] = useState<Array<string>>([])
  const [titleOptions, setTitleOptions] = useState<Array<string>>([])

  const listNames = useMemo(
    () => filteredList.map((prov) => prov.displayName),
    [filteredList]
  )

  const listSpecialties = useMemo<SpecialtyList>(() => {
    // step 1: get a unique set of all provider preferences
    const filteredSpecialtyNames = new Set<string>()
    providerList.forEach(
      (prov) =>
        (filters.specialtyArea === '' ||
          prov.specialtyAreaKeys.indexOf(filters.specialtyArea) >= 0) &&
        prov.specialties.forEach((sp) => filteredSpecialtyNames.add(sp))
    )

    // step 2: filter specialtyList by cross-referencing the set of preferences
    const filteredSpecialtyList = specialtyList.filter(
      (sp) =>
        filters.specialtyArea === '' ||
        (filters.specialtyArea === sp.groupBy &&
          filteredSpecialtyNames.has(sp.label))
    )

    // step 3: sort remaining array based on group by and specialty name
    const sortHelper = (a: SpecialtyListItem, b: SpecialtyListItem) => {
      if (a.groupBy === b.groupBy) {
        return a.label > b.label ? 1 : -1
      }
      const groupByA = specialtyAreaList.find(spa => spa.key === a.groupBy)!.teamPageOrder
      const groupByB = specialtyAreaList.find(spa => spa.key === b.groupBy)!.teamPageOrder
      return groupByA > groupByB ? -1 : 1
    }

    return filteredSpecialtyList.sort(sortHelper)
  }, [filters.specialtyArea, providerList.length])

  // This prevents accidental matches when one specialty is a substring of another.
  // If user input exactly matches a specialty option, don't allow partial matches
  // to a provider's specialties.
  const shouldUseExactMatch = useMemo<boolean>(
    () =>
      specialtyOptions.some(
        (opt) => opt.label.toLowerCase() === filters.specialty.toLowerCase()
      ),
    [filters.specialty]
  )

  const matchSpecialtyInput = (prov: ProviderListItem) =>
    prov.specialties.some((spec) =>
      shouldUseExactMatch
        ? spec.toLowerCase() === filters.specialty.toLowerCase()
        : spec.toLowerCase().includes(filters.specialty.toLowerCase())
    )

  useEffect(() => {
    setFilteredList(
      providerList.filter((prov) => {
        let shouldReturn = Boolean(
          filters.search === '' ||
            prov.displayName
              .toLowerCase()
              .includes(filters.search.toLowerCase())
        )
        shouldReturn =
          shouldReturn &&
          Boolean(
            filters.specialtyArea === '' ||
              prov.specialtyAreaKeys.indexOf(filters.specialtyArea) >= 0
          )

        shouldReturn =
          shouldReturn &&
          Boolean(filters.specialty === '' || matchSpecialtyInput(prov))

        shouldReturn =
          shouldReturn &&
          Boolean(filters.gender === '' || prov.gender === filters.gender)

        shouldReturn =
          shouldReturn &&
          Boolean(
            filters.language === '' || prov.languages.includes(filters.language)
          )

        shouldReturn =
          shouldReturn &&
          Boolean(
            filters.location === '' || prov.locations.includes(filters.location)
          )

        shouldReturn =
          shouldReturn &&
          Boolean(
            filters.title === '' || prov.displayTitle.includes(filters.title)
          )

        shouldReturn =
          shouldReturn &&
          Boolean(
            filters.age === '' ||
              (Number(filters.age) >= prov.minAge &&
                Number(filters.age) <= prov.maxAge)
          )

        return shouldReturn
      })
    )
  }, [filters])

  useEffect(() => {
    setFilteredList(providerList)
    if (providerList?.length) {
      const allLanguages = Array.prototype.concat.apply(
        [],
        providerList.map((prov) => prov.languages)
      )
      setLanguageOptions([...new Set(allLanguages)].sort())

      const allLocations = Array.prototype.concat.apply(
        [],
        providerList.map((prov) => prov.locations)
      )
      setLocationOptions([...new Set(allLocations)].sort())

      const allProviderTitles = Array.prototype.concat.apply(
        [],
        providerList.map((prov) => prov.displayTitle)
      )
      setTitleOptions([...new Set(allProviderTitles)].sort())

      setSpecialtyOptions(listSpecialties)
    }
  }, [providerList?.length])

  useEffect(() => {
    setSpecialtyAreaOptions(specialtyAreaList)
  }, [specialtyAreaList.length])

  useEffect(() => {
    if (providerList?.length) {
      setSpecialtyOptions(listSpecialties)
    }
  }, [filters.specialtyArea])

  const handleFilterChange = (value: { [key: string]: string }): void => {
    setFilters({ ...filters, ...value })
  }

  return {
    filteredList,
    handleFilterChange,
    specialtyAreaOptions,
    specialtyOptions,
    languageOptions,
    locationOptions,
    titleOptions,
    nameOptions: listNames,
    filtersApplied: filters,
  }
}
